import { types, getEnv, flow, Instance } from 'mobx-state-tree';
import { MtsTicket, prepareNewMtsTicket } from './mtsTicket';
import { ICommonAppEnvWithSBBaseApi, IOddsExtData, IOdd } from '../index';
import { ReuseAliveSelection } from '../../api/proto/apiContract';
import { ITicketStorageData } from './ticketStorage';
import { getCompetitorNamesFromEventName, getLongTipNameRaw, getRenameTipDataFromCompetitorNames, protoUtils } from 'eTip--common';
import { createTipIdent, createTipKey } from '../event';
import { OddsListItem } from '../../dtos/apiWebStandardEventDtos';
import { canConvertTipNameMts } from '../../utils/eventsUtils';

const OTBTicketIndex = 4;

export const MtsTicketStore = types
    .model({
        tickets: types.optional(types.array(MtsTicket), []),
        isCollapsed: types.optional(types.boolean, true),
        activeTicketIndex: types.optional(types.number, 0),
        lastUpdate: types.maybeNull(types.Date),
        isOTB: types.optional(types.boolean, false),
    })
    .views(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        return {
            get tabsCount(): number {
                return self.tickets.length - 1; //last one s for One tab bet
            },

            get activeTips(): { [key: string]: string } {
                let ret = {};
                self.tickets[self.activeTicketIndex].selections.forEach(selection => {
                    const s = selection.indent.split('_');
                    ret[`${s[0]}_${s[1]}`] = selection.indent;
                });
                return ret;
            },

            get inactiveTips(): { [key: string]: string } {
                let ret = {};
                self.tickets.forEach(ticket => {
                    if (self.tickets[self.activeTicketIndex] !== ticket) {
                        ticket.selections.forEach(selection => {
                            const s = selection.indent.split('_');
                            ret[`${s[0]}_${s[1]}`] = selection.indent;
                        })
                    }
                });
                return ret;
            },

            get tipsCount(): number {
                let cnt = 0;
                self.tickets.forEach(ticket => { cnt += ticket.selectionsCount })
                return cnt;
            },

            get canShowOneTapBet(): boolean {
                return env.settings.canShowOneTapBet;
            }
        }
    })
    .actions(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const init = (numberOfTickets: number): void => {
            self.lastUpdate = new Date();
            for (let i = 0; i < numberOfTickets; i++) {
                self.tickets.push(prepareNewMtsTicket(i, env));
            }
            loadTicketDataFromStorageAsync().then(() => {
                if (self.tickets[0].selections && self.tickets[0].selections.length > 0) {
                    self.tickets[0].validateTicketAsync({ showUpdateError: true, sourceForLog: "loadTicketDataFromStorageAsync", skipIfAlreadyInProgress: false }); //really async, do not need to wait
                }
            })
        };

        const getEmptyMtsTicket = (): number => {
            const totalCount = self.tabsCount;
            for (let counter = 0; counter < totalCount; counter++) {
                let index = (self.activeTicketIndex + counter) % totalCount;
                if (self.tickets[index].isMtsTicketEmpty()) {
                    return index;
                }
            }

            return self.activeTicketIndex;
        }

        const clearCouponAsync: () => Promise<void> = flow(function* () {
            yield self.tickets[self.activeTicketIndex].clearCouponAsync(false);
            self.isCollapsed = true;
        });

        const setActiveCoupon = (index: number): void => {
            self.activeTicketIndex = index;
        };

        /**
         * Toggle tip and send the coupon to the server to store it for potential page refresh.
         *
         */
        const oddsSetTipOnCouponAsync: (odd: IOdd, eventGameData: IOddsExtData, forceAdd?: boolean, canAnimate?: boolean) => Promise<void> = flow(function* (odd: IOdd, eventGameData: IOddsExtData, forceAdd?: boolean, canAnimate?: boolean) {
            const ticket = self.tickets[self.activeTicketIndex];
            const canAddRemoveTip = !self.isOTB || (self.isOTB && self.tickets[self.activeTicketIndex].isOTBSettingSet);

            if (!canAddRemoveTip) return;

            self.lastUpdate = new Date();
            if (!forceAdd && self.activeTips[odd.key]) {
                yield ticket.removeTipAsync(odd.ident, false);
            } else {
                yield ticket.addTipAsync(odd.ident, odd, eventGameData, forceAdd, canAnimate);
            }
        });

        const tabChange = (index: number): void => {
            self.activeTicketIndex = index;
        }

        const setTicketDataAsync: (ticket: ITicketStorageData, index: number) => Promise<void> = flow(function* (ticket: ITicketStorageData, index: number) {
            self.lastUpdate = new Date();
            if (ticket) {
                try {
                    self.tickets[index].setDataFromStorage(ticket);
                } catch (e) {
                    env.loggingService.error("Error refreshing ticket ", e);
                    yield self.tickets[index].clearCouponAsync(true);
                    return;
                }
            }

        });

        const loadTicketDataFromStorageAsync: () => Promise<void> = flow(function* () {
            self.lastUpdate = new Date();
            //although for logged user use anonymous data if exists to use it after login.
            for (let i = 0; i < self.tabsCount; i++) {
                const ticketAnonymousKey = env.ticketStorage.getKey(0, i);
                const ticketAnonymous = env.ticketStorage.load(ticketAnonymousKey, true);

                if (ticketAnonymous) {
                    yield setTicketDataAsync(ticketAnonymous, i);
                    if (env.loggedUserId > 0) {
                        env.ticketStorage.store(env.ticketStorage.getKey(env.loggedUserId, i), ticketAnonymous);
                        env.ticketStorage.remove(ticketAnonymousKey);
                    }
                }
                else {
                    if (env.loggedUserId > 0) {
                        const ticketLoggedKey = env.ticketStorage.getKey(env.loggedUserId, i);
                        yield setTicketDataAsync(env.ticketStorage.load(ticketLoggedKey, true), i);
                    }
                }
            }
        });

        const copyTicketTab: () => Promise<void> = flow(function* () {
            self.lastUpdate = new Date();

            let firstEmptyTab = -1;
            for (let i = self.tabsCount - 1; i >= 0; i--) {
                if (self.tickets[i].selections.length === 0) {
                    firstEmptyTab = i;
                }
            }
            if (firstEmptyTab > -1) {
                const ticketLoggedKey = env.ticketStorage.getKey(env.loggedUserId, self.activeTicketIndex);
                const activeTabData = env.ticketStorage.load(ticketLoggedKey, true);
                env.ticketStorage.store(env.ticketStorage.getKey(env.loggedUserId, firstEmptyTab), activeTabData);
                yield setTicketDataAsync(activeTabData, firstEmptyTab);
                tabChange(firstEmptyTab);
            }
        });

        /**
         * Expected for live with one tab
         */
        const getSelectionsToCompare = (): Map<string, number> => {
            return self.tickets[self.activeTicketIndex].getSelectionsToCompare();
        }

        /**
         *
         */
        const ratesChanged = (items: { id: string, rate: number }[]): void => {
            self.tickets[self.activeTicketIndex].ratesChanged(items);
        }

        const timerReload = (): void => {
            if (self.lastUpdate && (new Date().getTime() - self.lastUpdate.getTime() > 30000)) {
                self.lastUpdate = new Date();
                if (self.tickets[self.activeTicketIndex].canValidateTicket) {
                    self.tickets[self.activeTicketIndex].validateTicketAsync({ showUpdateError: true, sourceForLog: "timerReload", skipIfAlreadyInProgress: true });
                }
            }
        }

        const setIsOTB = (isOn: boolean) => {
            if (!isOn) {
                self.tickets[OTBTicketIndex].setIsOTBSettingSet(false);
                tabChange(0);
            }
            else {
                tabChange(OTBTicketIndex);
            }
            self.isOTB = isOn;
        }

        const assignSelectionsToTicketAsync: (selections: ReuseAliveSelection[], canAnimate: boolean) => Promise<void> = flow(function* (selections: ReuseAliveSelection[], canAnimate: boolean) {

            const availableTab = getEmptyMtsTicket();
            setActiveCoupon(availableTab);

            for (let i = 0; i < selections.length; i++) {

                const selection = selections[i];
                const tipName = canConvertTipNameMts(selection) ? getLongTipNameRaw(selection.TipName, getRenameTipDataFromCompetitorNames(getCompetitorNamesFromEventName(selection.FixtureName))) : selection.TipName;
                const oddsItem: OddsListItem = {
                    DetailID: protoUtils.protoLongNumberToNumber(selection.DetailId),
                    EventID: protoUtils.protoLongNumberToNumber(selection.FixtureId),
                    GameID: selection.GameId,
                    Name: tipName,
                    PopularityPercentage: 0,
                    Rate: selection.Rate,
                    TipID: selection.TipId.toString(),
                    TicketOfDayTip: null,
                    State: null
                }

                const ident = createTipIdent(oddsItem);
                const odd: IOdd = {
                    ident: ident,
                    name: null, // for "rate" button only
                    key: createTipKey(oddsItem),
                    nameForTicket: tipName,
                    rate: selection.Rate,
                    isSuspended: null,
                    popularity: 0,
                    isHighlighted: false
                };

                const eventGameData: IOddsExtData = {
                    eventName: selection.FixtureName,
                    gameName: selection.DetailName,
                    hasDetail: selection.DetailName ? true : false,
                    detailName: selection.DetailName,
                    eventDate: protoUtils.protoTimeStamp2Date(selection.FixtureDate),
                    number: protoUtils.protoLongNumberToNumber(selection.Number),
                    offerType: selection.OfferType
                };

                yield oddsSetTipOnCouponAsync(odd, eventGameData, true, canAnimate);
            }

            const ticket = self.tickets[availableTab];

            // do not wait for result of validateTicketAsync
            ticket.validateTicketAsync({ showUpdateError: false, sourceForLog: 'assignSelectionsToTicketAsync' });
        });

        return {
            init,
            setActiveCoupon,
            oddsSetTipOnCouponAsync,
            clearCouponAsync,
            tabChange,
            copyTicketTab,
            getSelectionsToCompare,
            ratesChanged,
            timerReload,
            setIsOTB,
            getEmptyMtsTicket,
            assignSelectionsToTicketAsync
        };
    });

export type IMtsTicketStore = Instance<typeof MtsTicketStore>;